import { clusterReward } from '../../../packages/domain/src/56-score-board-games.mjs';
import { gameCellArtwork } from './36-game-artwork.mjs';
import { gameColourPresentation } from './35-game-board.mjs';
import { paintIconAction } from './82-icon-actions.mjs';

export const BUBBLE_BURST_ATLAS = new URL('../assets/game-bubble-burst-v2.webp', import.meta.url)
  .href;
export const BUBBLE_MATERIAL_ATLAS = new URL(
  '../assets/game-bubble-materials-v2.webp',
  import.meta.url,
).href;

/** A finite hands-on rehearsal uses canonical reward examples and isolated
 * demonstration art. It never moves the real board, advances a clock, grants
 * points or creates a transcript. Skip/Escape and failed image loads stay usable. */
export function createClusterGuide(ctx, { playfield, board, auto = false }) {
  const panel = ctx.e('section', 'bubble-guide');
  panel.setAttribute('role', 'region');
  panel.setAttribute('aria-label', 'Bubble Grove introduction');
  panel.hidden = true;
  const eyebrow = ctx.e('span', 'bubble-guide-eyebrow', 'A LITTLE PRACTICE'),
    title = ctx.e('strong', 'bubble-guide-title'),
    caption = ctx.e('p', 'bubble-guide-caption'),
    stage = ctx.e('div', 'bubble-guide-stage'),
    result = ctx.e('p', 'bubble-guide-result'),
    actions = ctx.e('div', 'bubble-guide-actions'),
    count = ctx.e('span', 'bubble-guide-count'),
    skip = ctx.e('button', 'game-action'),
    next = ctx.e('button', 'game-action');
  result.setAttribute('role', 'status');
  skip.type = next.type = 'button';
  paintIconAction(ctx, skip, 'x', 'Skip introduction');
  actions.append(count, skip, next);
  panel.append(eyebrow, title, caption, stage, result, actions);
  playfield.append(panel);
  const storageKey = 'myaudience-bubble-guide-v2';
  let step = 0,
    rehearsed = false,
    disposed = false,
    warmed = false;
  const images = [];
  function warm() {
    if (warmed || typeof window.Image !== 'function') return;
    warmed = true;
    for (const url of [
      BUBBLE_BURST_ATLAS,
      BUBBLE_MATERIAL_ATLAS,
      new URL('../assets/game-bubble-grove-world-v2.webp', import.meta.url).href,
    ]) {
      const image = new window.Image();
      images.push(image);
      image.src = url;
      image.decode?.().catch(() => {});
    }
  }
  function orb(colour, row = 0) {
    const node = ctx.e('span', 'bubble-guide-orb');
    node.style.setProperty('--piece-colour', `var(--game-colour-${colour})`);
    node.style.setProperty('--bubble-row', String(row));
    const cell = { value: colour, colour };
    node.append(
      gameCellArtwork(
        ctx,
        { gameId: 'cluster-pop' },
        cell,
        0,
        gameColourPresentation(colour).shape,
        '',
      ),
    );
    const material = ctx.e('span', 'bubble-guide-material');
    material.style.backgroundPosition = `${(colour % 3) * 50}% ${Math.floor(colour / 3) * 100}%`;
    const glyph = ctx.e('span', 'bubble-guide-symbol', gameColourPresentation(colour).shape);
    material.setAttribute('aria-hidden', 'true');
    glyph.setAttribute('aria-hidden', 'true');
    node.append(material, glyph);
    return node;
  }
  function rehearse(button) {
    if (rehearsed || disposed || panel.hidden) return;
    rehearsed = true;
    button.disabled = true;
    stage.dataset.popped = 'true';
    const reward = clusterReward({ combo: step === 1 ? 1 : 0, level: 1 }, step === 1 ? 4 : 3);
    result.textContent =
      step === 1
        ? `Combo 2 · +${reward.points} = 120 + 25 bonus`
        : `Lovely pop! +${reward.points} practice points`;
    const sprite = ctx.e('span', 'bubble-guide-burst');
    sprite.setAttribute('aria-hidden', 'true');
    stage.append(sprite);
    paintIconAction(ctx, next, 'arrow-right', 'Next step', 'Next');
  }
  function paint() {
    rehearsed = false;
    stage.replaceChildren();
    stage.dataset.step = String(step);
    delete stage.dataset.popped;
    result.textContent = '';
    count.textContent = `${step + 1} / 3`;
    title.textContent = ['Find your first pop', 'Build a bonus chain', 'Let the garden fall'][step];
    caption.textContent = [
      'Tap two or more matching bubbles that touch. Try this glowing group of three.',
      'A group of 4 starts Combo 1. Pop another 4+ to build it up to five. Groups of 2–3 reset it.',
      'Bubbles fall straight down. New ones arrive from above. Bigger groups earn much bigger bursts.',
    ][step];
    if (step < 2) {
      const target = ctx.e('button', 'bubble-guide-target');
      target.type = 'button';
      target.setAttribute(
        'aria-label',
        step === 0 ? 'Pop three matching practice bubbles' : 'Pop four matching practice bubbles',
      );
      const group = ctx.e('span', 'bubble-guide-group');
      for (let i = 0; i < (step === 0 ? 3 : 4); i++) group.append(orb(step));
      target.append(group);
      if (ctx.icon) target.append(ctx.icon('hand'));
      target.addEventListener('click', () => rehearse(target));
      stage.append(target);
      result.textContent = 'Practice only · Your game is waiting';
    } else {
      const fall = ctx.e('div', 'bubble-guide-fall');
      fall.setAttribute('aria-hidden', 'true');
      for (let row = 0; row < 3; row++)
        for (let column = 0; column < 4; column++) fall.append(orb((column + row) % 4, row));
      stage.append(fall);
      result.textContent =
        'Rush goals: 3 big pops in 10s, 5 in 15s. Choose 2:00 when you are ready.';
    }
    paintIconAction(
      ctx,
      next,
      step === 2 ? 'play' : 'arrow-right',
      step === 2 ? 'Play now' : 'Next step',
      step === 2 ? 'Play' : 'Next',
    );
  }
  function cancel() {
    panel.hidden = true;
    board.inert = false;
  }
  function close() {
    cancel();
    try {
      window.sessionStorage?.setItem(storageKey, 'seen');
    } catch {
      // Optional tab memory never gates access to the game.
    }
    [...board.children].find((cell) => !cell.disabled)?.focus?.({ preventScroll: true });
  }
  function show(focus = true) {
    if (disposed) return;
    step = 0;
    warm();
    paint();
    panel.hidden = false;
    board.inert = true;
    if (focus) skip.focus?.({ preventScroll: true });
  }
  skip.addEventListener('click', close);
  next.addEventListener('click', () => {
    if (step === 2) close();
    else {
      step++;
      paint();
    }
  });
  panel.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    close();
  });
  let seen = false;
  try {
    seen = window.sessionStorage?.getItem(storageKey) === 'seen';
  } catch {
    // The guide remains usable when browser storage is unavailable.
  }
  if (auto && !seen) show(false);
  return {
    show,
    cancel,
    dispose() {
      disposed = true;
      cancel();
      for (const image of images) image.removeAttribute('src');
      panel.remove();
    },
  };
}
